import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { AssetUrls } from 'config/assets';
import { useDeleteUserAccountMutation } from 'store/services/admin/adminApi';
import './DeleteAccountModal.css';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  username: string;
  onSuccess: () => void;
}

// Case-sensitive confirmation phrase — stricter than the ban modal's case-insensitive match.
const CONFIRM_PHRASE = 'DELETE';

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  userId,
  username,
  onSuccess,
}) => {
  const [reason, setReason] = useState<string>('');
  const [confirmText, setConfirmText] = useState<string>('');
  const [phraseText, setPhraseText] = useState<string>('');
  const [renameUsername, setRenameUsername] = useState<boolean>(false);
  const [deleteUserAccount, { isLoading }] = useDeleteUserAccountMutation();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setConfirmText('');
      setPhraseText('');
      setRenameUsername(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isReasonValid = reason.trim().length >= 10 && reason.trim().length <= 1000;
  const isUsernameConfirmed = confirmText.toLowerCase() === username.toLowerCase();
  const isPhraseConfirmed = phraseText === CONFIRM_PHRASE;
  const isValid = isReasonValid && isUsernameConfirmed && isPhraseConfirmed;

  const handleSubmit = async () => {
    if (!isValid) return;

    try {
      await deleteUserAccount({
        userId,
        body: {
          reason: reason.trim(),
          renameUsername,
        },
      }).unwrap();

      toast.success(`Account for ${username} has been deleted.`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to delete account:', error);
      toast.error(error.data?.message || 'Failed to delete account. Please try again.');
    }
  };

  const modalContent = (
    <div className="delete-account-overlay" onClick={onClose}>
      <div className="delete-account-modal" onClick={(e) => e.stopPropagation()}>
        <div className="delete-account-header">
          <h2 className="delete-account-title">Delete User Account</h2>
          <button className="delete-account-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="delete-account-content">
          <div className="delete-account-danger-banner">
            <span aria-hidden="true">⚠️</span>
            <span>
              <strong>Permanent action.</strong> This anonymizes the account — the
              user&apos;s email, phone number, name, shipping address, and profile details
              (bio, avatar, custom URL) are permanently wiped. The username is preserved by
              default so historical trades stay readable to other users. This cannot be
              undone.
            </span>
          </div>

          <div className="delete-account-info-section">
            <div className="delete-account-info-row">
              <span className="delete-account-info-label">User to delete:</span>
              <span className="delete-account-info-value delete-account-username-value">
                {username}
              </span>
            </div>
          </div>

          <div className="delete-account-form-group">
            <label htmlFor="delete-reason" className="delete-account-form-label">
              Reason for deletion
              <span className="delete-account-required">*</span>
            </label>
            <textarea
              id="delete-reason"
              className="delete-account-form-textarea"
              placeholder="Provide a detailed explanation for this deletion (10-1000 characters)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={5}
              maxLength={1000}
              disabled={isLoading}
            />
            <CharacterCounter
              currentLength={reason.length}
              maxLength={1000}
              warningThreshold={950}
            />
            {reason.length > 0 && reason.trim().length < 10 && (
              <div className="delete-account-validation-hint error">
                Reason must be at least 10 characters
              </div>
            )}
          </div>

          <div className="delete-account-form-group">
            <label className="delete-account-checkbox-row">
              <input
                type="checkbox"
                checked={renameUsername}
                onChange={(e) => setRenameUsername(e.target.checked)}
                disabled={isLoading}
              />
              <span>
                Also rename the username to a placeholder (
                <code>deleted_user_{userId}</code>). Use this only when the username is the
                person&apos;s real name.
              </span>
            </label>
          </div>

          <div className="delete-account-form-group">
            <label htmlFor="delete-confirm-username" className="delete-account-form-label">
              Type <strong>&quot;{username}&quot;</strong> to confirm
              <span className="delete-account-required">*</span>
            </label>
            <input
              id="delete-confirm-username"
              type="text"
              className="delete-account-form-input"
              placeholder={`Type ${username} to confirm`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={isLoading}
            />
            {confirmText && !isUsernameConfirmed && (
              <div className="delete-account-validation-hint error">
                Username does not match
              </div>
            )}
          </div>

          <div className="delete-account-form-group">
            <label htmlFor="delete-confirm-phrase" className="delete-account-form-label">
              Type <strong>{CONFIRM_PHRASE}</strong> to confirm this is irreversible
              <span className="delete-account-required">*</span>
            </label>
            <input
              id="delete-confirm-phrase"
              type="text"
              className="delete-account-form-input"
              placeholder={`Type ${CONFIRM_PHRASE}`}
              value={phraseText}
              onChange={(e) => setPhraseText(e.target.value)}
              disabled={isLoading}
            />
            {phraseText && !isPhraseConfirmed && (
              <div className="delete-account-validation-hint error">
                Must match exactly: {CONFIRM_PHRASE}
              </div>
            )}
          </div>

          <div className="delete-account-effects-list">
            <h3 className="delete-account-section-subtitle">
              What happens when an account is deleted:
            </h3>
            <ul>
              <li>
                The user is logged out of all sessions and the login credential is removed —
                the account can never be logged into again.
              </li>
              <li>
                Email, phone number, name, shipping address, and profile details are
                permanently wiped.
              </li>
              <li>The username is preserved unless you check the rename box above.</li>
              <li>
                The email is added to the email suppression list and any active subscription
                is cancelled.
              </li>
              <li>
                Account status becomes <strong>deleted</strong>. Unlike a ban, this is NOT
                reversible.
              </li>
              <li>This action is recorded in the admin audit trail.</li>
            </ul>
          </div>
        </div>

        <div className="delete-account-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={onClose}
            isDisabled={isLoading}
            fullWidth={false}
          />
          <MagicButton
            label={isLoading ? 'Deleting Account...' : 'Delete Account'}
            color="default"
            onPress={handleSubmit}
            isLoading={isLoading}
            isDisabled={!isValid || isLoading}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default DeleteAccountModal;
